import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import CustomerLayout from '../CustomerLayout';

const SubscriptionManagement = () => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);

  const [waitlistForm, setWaitlistForm] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    shipmentVolume: '',
  });

  const handleChange = (e) => {
    setWaitlistForm({
      ...waitlistForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleJoinWaitlist = () => {
    console.log('Business membership waitlist:', waitlistForm);

    // Later connect this to backend:
    // POST /api/membership/waitlist

    setWaitlistSubmitted(true);
  };

  return (
    <CustomerLayout>
      <Box sx={{ maxWidth: 1150, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Access Plans
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage your current HaggleFreight access and join future business membership options.
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          Early Access is currently free for approved HaggleFreight users during the platform rollout.
        </Alert>

        <Grid container spacing={3} alignItems="stretch">
          {/* EARLY ACCESS */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                border: '2px solid #1565c0',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    Early Access
                  </Typography>
                  <Chip label="ACTIVE" color="primary" size="small" />
                </Stack>

                <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                  Free
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  For early customers using HaggleFreight before the full public rollout.
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <List dense>
                  <ListItem>
                    <ListItemText primary="Post and manage loads" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Track load status updates" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Receive driver and carrier bids" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Use bidding and negotiation tools" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access platform updates during rollout" />
                  </ListItem>
                </List>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, py: 1.3, fontWeight: 'bold' }}
                onClick={() => setDetailsOpen(true)}
              >
                View Access Details
              </Button>
            </Paper>
          </Grid>

          {/* BUSINESS MEMBERSHIP */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: 3,
                border: '1px solid #d0d7de',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    Business Membership
                  </Typography>
                  <Chip label="WAITLIST OPEN" color="success" size="small" />
                </Stack>

                <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                  Coming Soon
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  For companies with recurring transportation, freight, and logistics needs.
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <List dense>
                  <ListItem>
                    <ListItemText primary="Bulk load posting" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Business account tools" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Team access and account management" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Priority business onboarding" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Future analytics and reporting tools" />
                  </ListItem>
                </List>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                sx={{ mt: 3, py: 1.3, fontWeight: 'bold' }}
                onClick={() => {
                  setWaitlistSubmitted(false);
                  setWaitlistOpen(true);
                }}
              >
                Join Business Waitlist
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* EARLY ACCESS DETAILS */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle fontWeight="bold">Early Access Details</DialogTitle>

          <DialogContent dividers>
            <Typography variant="subtitle1" fontWeight="bold">
              Current Access
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Your account currently has Early Access to HaggleFreight.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold">
              Billing
            </Typography>
            <Typography sx={{ mb: 2 }}>
              No monthly membership fee is currently charged during the platform rollout.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold">
              Included Features
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText primary="Load posting and management" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Load tracking and status updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Driver and carrier bidding and negotiation tools" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Early access to new platform features" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Ability to provide product feedback before full rollout" />
              </ListItem>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              Future paid membership pricing will be announced before any paid plans are activated.
            </Alert>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            <Button variant="contained" onClick={() => setDetailsOpen(false)}>
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* WAITLIST MODAL */}
        <Dialog open={waitlistOpen} onClose={() => setWaitlistOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle fontWeight="bold">Join Business Membership Waitlist</DialogTitle>

          <DialogContent dividers>
            {!waitlistSubmitted ? (
              <>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Join the waitlist to receive early business onboarding, membership updates, and access
                  information when business plans become available.
                </Typography>

                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={waitlistForm.businessName}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Contact Name"
                  name="contactName"
                  value={waitlistForm.contactName}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={waitlistForm.email}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={waitlistForm.phone}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Estimated Monthly Shipment Volume"
                  name="shipmentVolume"
                  placeholder="Example: 10–50 shipments per month"
                  value={waitlistForm.shipmentVolume}
                  onChange={handleChange}
                />
              </>
            ) : (
              <Alert severity="success">
                You have been added to the HaggleFreight Business Membership waitlist.
              </Alert>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setWaitlistOpen(false)}>Close</Button>

            {!waitlistSubmitted && (
              <Button
                variant="contained"
                onClick={handleJoinWaitlist}
                disabled={
                  !waitlistForm.businessName ||
                  !waitlistForm.contactName ||
                  !waitlistForm.email
                }
              >
                Join Waitlist
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </CustomerLayout>
  );
};

export default SubscriptionManagement;